import React from 'react';
import { Card } from '../../Library/Card';
import { Box, Grid, Typography } from '@mui/material';
import {
  CortisolResult,
  HormoneSnapshot,
  StressInsightsColors,
} from '../../../types';
import { roundNumber } from '../../../utils';
import { colors } from '../../../utils/theme';

enum SampleTimes {
  'waking',
  'mid-day',
  'bedtime',
}

export type DescriptorData = {
  color: StressInsightsColors;
  label: string;
};

export const CortisolMetricsDescriptor = ({
  currentSnapshot,
  prevSnapshot,
}: {
  currentSnapshot: HormoneSnapshot;
  prevSnapshot?: HormoneSnapshot | null;
}) => {
  const data: CortisolResult[] = currentSnapshot.test_results.map(
    (result, resultIdx) => {
      let diff;
      if (prevSnapshot) {
        diff = roundNumber(
          result.result - prevSnapshot.test_results[resultIdx].result
        );
      }
      return {
        time: SampleTimes[resultIdx] || `sample ${resultIdx + 1}`,
        high: roundNumber(result.max_value),
        average: roundNumber((result.min_value + result.max_value) / 2),
        low: roundNumber(result.min_value),
        result: roundNumber(result.result),
        diff,
      };
    }
  );

  const determineDescriptorData = (result: CortisolResult): DescriptorData => {
    if (result.result > result.high) {
      return {
        color: StressInsightsColors.EXHAUSTED,
        label: 'High',
      };
    }
    if (result.result < result.low) {
      return {
        color: StressInsightsColors.ALARMED,
        label: 'Low',
      };
    }
    return {
      color: StressInsightsColors.NORMAL,
      label: 'Normal',
    };
  };

  return (
    <Card
      content={
        <Grid container flexDirection="row" justifyContent="space-between">
          {data?.map((result: CortisolResult, index: number) => (
            <Grid item key={`cortisol-descriptor-${index}`}>
              <Grid container flexDirection="column">
                <Grid container item flexDirection="row" alignItems="center">
                  <div
                    style={{
                      width: 6,
                      height: 6,
                      borderRadius: 50,
                      marginRight: 5,
                      background: colors.greys.BRAND_BLACK,
                    }}
                  />
                  <Typography variant="light" fontSize={12}>
                    {result.time.toUpperCase()}
                  </Typography>
                </Grid>
                <div
                  style={{
                    height: 0.5,
                    background: colors.greys.BRAND_BLACK,
                    marginTop: 4,
                    marginBottom: 4,
                  }}
                />
                <Typography
                  fontSize={20}
                  variant="mono"
                  sx={{
                    color: determineDescriptorData(result).color,
                    my: 0.25,
                  }}
                >
                  {result.result}
                </Typography>
                {result.diff !== undefined && (
                  <Box sx={{ display: 'inline-block' }}>
                    <Typography
                      variant="mono"
                      fontSize={10}
                      sx={{
                        background: determineDescriptorData(result).color,
                        borderRadius: 0.5,
                        py: 0.25,
                        px: 0.5,
                        color: colors.greys.BASE_WHITE,
                      }}
                    >
                      {`${result.diff > 0 ? '+' : ''}${result.diff}`}
                    </Typography>
                  </Box>
                )}

                <Typography
                  fontSize={12}
                  variant="light"
                  sx={{ color: colors.greys.BLACK_900, mt: 0.5 }}
                >
                  {determineDescriptorData(result).label}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      }
    />
  );
};
